import { useState } from 'preact/hooks';
import { Wordmark } from '../components/Wordmark';
import { Button } from '../components/Button';
import { api } from '../lib/api';
import { usePageMeta } from '../lib/meta';

export function SignIn() {
  usePageMeta('Sign in — shotmeal');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: SubmitEvent) {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    setError(null);
    try {
      await api('/api/auth/magic-link', {
        method: 'POST',
        body: JSON.stringify({ email, locale: 'en' }),
      });
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send link');
    } finally {
      setSending(false);
    }
  }

  return (
    <main class="page-in" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '24px 24px' }}>
        <a href="/" style={{ textDecoration: 'none', display: 'inline-flex' }}>
          <Wordmark size={26} />
        </a>
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            background: 'var(--card)',
            borderRadius: 28,
            padding: 32,
            boxShadow: 'var(--shadow-card)',
            maxWidth: 440,
            width: '100%',
          }}
        >
          <h1 class="wordmark" style={{ margin: 0, fontSize: 40, letterSpacing: '-0.02em' }}>
            Sign in to back up.
          </h1>
          <p style={{ marginTop: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
            Optional. shotmeal works fully offline — sign in to back up your
            diary in the cloud and view your meals and stats here on the web.
          </p>

          <GoogleButton />
          <Divider />

          {sent ? (
            <div
              style={{
                marginTop: 24,
                padding: 18,
                borderRadius: 16,
                background: 'var(--ember-tint)',
                color: 'var(--ink)',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16 }}>Check your inbox</div>
              <div style={{ marginTop: 4, fontSize: 14, color: 'var(--ink-2)' }}>
                We sent a one-time sign-in link to <strong>{email}</strong>. It
                expires in 15 minutes.
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
                Email address
              </label>
              <input
                type="email"
                value={email}
                onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                placeholder="you@example.com"
                required
                style={{
                  background: 'var(--surface-2)',
                  border: 'none',
                  borderRadius: 12,
                  padding: '14px 16px',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 16,
                  color: 'var(--ink)',
                  outline: 'none',
                }}
              />
              {error && (
                <div style={{ color: 'var(--wine)', fontSize: 13 }}>{error}</div>
              )}
              <Button type="submit" variant="ember" full disabled={sending}>
                {sending ? 'Sending…' : 'Send sign-in link'}
              </Button>
            </form>
          )}

          <div
            style={{
              marginTop: 24,
              padding: 14,
              background: 'var(--surface-2)',
              borderRadius: 12,
              fontSize: 12,
              color: 'var(--ink-2)',
              lineHeight: 1.5,
            }}
          >
            By signing in you agree to our{' '}
            <a href="/terms" style={{ color: 'var(--ink)', fontWeight: 600 }}>Terms</a>{' '}
            and{' '}
            <a href="/privacy" style={{ color: 'var(--ink)', fontWeight: 600 }}>Privacy Policy</a>.
          </div>
        </div>
      </div>
    </main>
  );
}

// A full-page navigation to the backend's Google OAuth entry point. The backend
// redirects to Google, handles the callback, sets the sm_refresh cookie, and
// sends the browser to /auth/callback — where AuthCallback restores the session.
// (localStorage['sm_next'], stashed by RequireSession, survives the round-trip.)
function GoogleButton() {
  return (
    <button
      type="button"
      onClick={() => { window.location.href = '/api/auth/google'; }}
      style={{
        marginTop: 24,
        width: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '13px 18px',
        borderRadius: 14,
        border: 'none',
        boxShadow: '0 0 0 1px var(--line)',
        background: 'var(--card)',
        color: 'var(--ink)',
        fontFamily: 'var(--font-ui)',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      <GoogleG />
      Continue with Google
    </button>
  );
}

// Official multi-colour Google "G". Inlined as SVG so it needs no external
// request and renders identically in light/dark.
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  );
}

// "or" separator between the Google button and the email form.
function Divider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 0' }}>
      <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
      <span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>or</span>
      <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
    </div>
  );
}
